import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { IconPlus, IconUsers, IconX } from "@/components/Icons";
import { FieldSetForm } from "@/components/form/FieldSetForm";
import {
  blankRow,
  connectionColumns,
  resolveConnections,
  resolveEntrants,
  type GridRow,
} from "@/lib/grid";
import { appointmentsToInput, blankAppointments } from "@/lib/appointments";
import { Appointments } from "./Appointments";
import { EntryGrid } from "./EntryGrid";
import { createFixtureAction, updateFixtureAction } from "@/lib/actions/fixtures";
import type { EntryInput, FixtureFormContext, FormOption, SlotOptions } from "@/lib/api/types";
import { coerceToBlob, defaultValueFor, type FormValue } from "@/lib/form";

import styles from "./FixtureForm.module.css";

const STATUSES = [
  { id: "SCHEDULED", label: "Scheduled" },
  { id: "LIVE", label: "In progress" },
  { id: "COMPLETED", label: "Finished" },
];

/** The sentinel an entrant dropdown carries while it is a TBD placeholder,
 *  rather than a real party. Never sent to the server — mapped to a
 *  ``placeholderLabel`` at submit. */
const TBD = "__TBD__";

/** One entrant as loaded from an existing fixture, for the edit form to prefill. */
export type EntryInitial = {
  participant?: string;
  placeholderLabel?: string;
  alignment?: string;
  rank?: string;
};

/** The current state of a fixture the edit form opens on. */
export type FixtureInitial = {
  name?: string;
  venue?: string;
  scheduledStart?: string;
  status?: string;
  competition?: string;
  format?: Record<string, FormValue>;
  entries?: EntryInitial[];
};

/** When present, the form edits an existing fixture (PATCH) rather than
 *  creating one. ``code`` is fixed; the entrants and details are prefilled. */
export type EditingFixture = { id: string; code: string; initial: FixtureInitial };

type Entry = {
  participant: string;
  /** The free-text label when ``participant`` is the TBD sentinel. */
  placeholder: string;
  alignment: string;
  rank: string;
  /** The draw — lane, stall, grid slot. Known when the card is filed. */
  entry: Record<string, FormValue>;
  /** The figures. Known after. */
  metric: Record<string, FormValue>;
  /** An unordered lineup, by participant code. */
  lineup: string[];
  /** A named lineup, one entry per declared place ("Lead"…"Anchor"). */
  members: string[];
  /**
   * role code → one participant code per appointment.
   *
   * A list, not a single value: a runner has one jockey and one trainer but may
   * have forty owners, and the slot's own bounds say which.
   */
  connections: Record<string, string[]>;
};

function blankEntry(alignment = "", places = 0, slots: SlotOptions[] = []): Entry {
  return {
    participant: "",
    placeholder: "",
    alignment,
    rank: "",
    entry: {},
    metric: {},
    lineup: [],
    members: Array.from({ length: places }, () => ""),
    connections: blankAppointments(slots),
  };
}

function slug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * Fixture entry, generated from the fixture type being entered.
 *
 * Nothing here is per-sport code. A race asks for a field of runners and the
 * jockey on each; a league match asks for two sides and the eleven they field;
 * a relay asks for a club and the four swimmers in leg order — from the same
 * component, because the type says which of those this fixture is.
 *
 * The type is the unit, not the sport: swimming's individual event and its
 * relay render differently from the same screen, which one shape per sport
 * could never do.
 */
export function FixtureForm({
  context,
  editing,
}: {
  context: FixtureFormContext;
  editing?: EditingFixture;
}) {
  const navigate = useNavigate();
  const type = context.typeDef;
  // Everything that is not two sides meeting is entered as a list.
  const isField = type ? type.outcome !== "DUEL" : false;
  const alignments = type?.alignments ?? [];
  const composition = type?.composition ?? null;
  /** Named places a lineup fills, in order — "Lead", "Second", "Third", "Anchor". */
  const places = composition?.slots ?? [];
  /**
   * A grid, or a card per entry?
   *
   * A ranked field with nothing else hanging off it — a race, a heat, a card of
   * twenty runners — is a start list, and a start list is a grid. Anything with
   * named places or a lineup keeps the cards, because a relay leg and a batting
   * order are not columns and squeezing them into some would be worse than the
   * screen this replaces.
   */
  const useGrid = isField && (composition?.slots ?? []).length === 0 && !context.lineupLabel;

  /** How many entries this type opens with, and the fewest it will accept. */
  const minEntries = type?.entrant?.min ?? (isField ? 2 : 2);

  // One answer, not one per level. The chain of dropdowns could only walk a single
  // path from `fixtureParent` upward, so cricket's tour and its two series — five of
  // its ten fixtures — were places the screen could not file anything.
  const initial = editing?.initial;
  const [filedUnder, setFiledUnder] = useState(initial?.competition ?? "");
  const [venue, setVenue] = useState(initial?.venue ?? "");
  // A datetime-local input wants "YYYY-MM-DDTHH:mm"; an ISO instant is trimmed to it.
  const [start, setStart] = useState(initial?.scheduledStart ? initial.scheduledStart.slice(0, 16) : "");
  const [status, setStatus] = useState(initial?.status ?? "SCHEDULED");
  const [name, setName] = useState(initial?.name ?? "");
  const [code, setCode] = useState(editing?.code ?? "");
  const [format, setFormat] = useState<Record<string, FormValue>>(() => {
    const init: Record<string, FormValue> = {};
    for (const field of context.formatFields) init[field.key] = defaultValueFor(field);
    return { ...init, ...(initial?.format ?? {}) };
  });
  const [entries, setEntries] = useState<Entry[]>(() => {
    const slots = type?.composition?.slots ?? [];
    if (initial?.entries && initial.entries.length > 0) {
      // Prefill from the existing fixture: a real party keeps its code, a TBD
      // slot comes back as the sentinel plus its label.
      return initial.entries.map((e, i) => ({
        ...blankEntry(
          e.alignment ?? (type?.outcome === "DUEL" ? (type.alignments?.[i] ?? "") : ""),
          slots.length,
          context.connections,
        ),
        participant: e.placeholderLabel ? TBD : (e.participant ?? ""),
        placeholder: e.placeholderLabel ?? "",
        rank: e.rank ?? "",
      }));
    }
    const opening = Math.max(type?.entrant?.min ?? 2, 2);
    return Array.from({ length: opening }, (_, i) =>
      blankEntry(
        type?.outcome === "DUEL" ? (type.alignments?.[i] ?? "") : "",
        slots.length,
        context.connections,
      ),
    );
  });
  // The grid keeps its own rows: they are what the operator typed, names and
  // all, and only become entries at submit. Round-tripping them through `Entry`
  // on every keystroke would mean resolving a half-typed name forty times a
  // second and losing whatever did not match.
  const [gridRows, setGridRows] = useState<GridRow[]>(() => [blankRow()]);
  // Extra connection columns the operator opened — a second owner, a third.
  // Column-wise rather than row-wise, because a syndicate is still one runner.
  const [extraColumns, setExtraColumns] = useState<Record<string, number>>({});
  const [officials, setOfficials] = useState<Record<string, string[]>>(() =>
    blankAppointments(context.officials),
  );
  const [busy, setBusy] = useState(false);
  const [scoringProgram, setScoringProgram] = useState(
    context.suggestedScoringProgram ?? context.scoringPrograms[0]?.id ?? "",
  );
  const [scoringUi, setScoringUi] = useState(
    context.suggestedScoringUi ?? context.scoringUis[0]?.id ?? "",
  );
  const [errors, setErrors] = useState<string[] | null>(null);
  // Why an edit was made — recorded on the amendment. Edit mode only.
  const [reason, setReason] = useState("");

  const entrantsByCode = useMemo(
    () => new Map(context.entrants.map((e) => [e.code, e])),
    [context.entrants],
  );

  /** The name a fixture gets for free: "Mumbai v Chennai", or the container. */
  const suggestedName = useMemo(() => {
    const picked = entries.map((e) => entrantsByCode.get(e.participant)?.name).filter(Boolean);
    if (!isField && picked.length >= 2) return `${picked[0]} v ${picked[1]}`;
    const under = context.containers.find((c) => c.code === filedUnder);
    return under ? `${under.name} — ${context.entrantLabel.toLowerCase()}s` : "";
  }, [entries, entrantsByCode, isField, context.containers, context.entrantLabel, filedUnder]);

  const effectiveName = name || suggestedName;
  const effectiveCode = code || (effectiveName ? slug(effectiveName).slice(0, 60) : "");

  const editEntry = (i: number, change: Partial<Entry>) =>
    setEntries((prev) => prev.map((e, j) => (j === i ? { ...e, ...change } : e)));

  const lineupFor = (entry: Entry): FormOption[] =>
    context.lineup.filter((p) => !p.parent || p.parent === entry.participant);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setErrors(null);

    // The grid's rows are names as typed; resolving them here means an
    // unmatched one reaches the server as itself and comes back as "unknown
    // participant 'Rohit Sharm'", which is the truth, rather than vanishing.
    const gridConnections = connectionColumns(context.connections, extraColumns);
    const payloadEntries: EntryInput[] = useGrid
      ? resolveEntrants(gridRows, context.entrants).map((row, i) => {
          // In the grid, a TBD slot is typed as "TBD: <label>" — an unmatched
          // name the server would otherwise reject.
          const tbd = /^tbd:\s*/i.exec(row.entrant);
          if (tbd) return { placeholderLabel: row.entrant.slice(tbd[0].length).trim() };
          return {
            participant: row.entrant,
            rank: i + 1,
            entry: coerceToBlob(context.entryFields, row.values),
            connections: resolveConnections(row, gridConnections),
          };
        })
      : entries
      .filter((e) => e.participant && (e.participant !== TBD || e.placeholder.trim()))
      .map((e, i) => {
        // A TBD placeholder slot is a bare label — it fields nobody.
        if (e.participant === TBD) return { placeholderLabel: e.placeholder.trim() };
        return {
          participant: e.participant,
          alignment: e.alignment || undefined,
          rank: isField ? (e.rank ? Number(e.rank) : i + 1) : undefined,
          entry: coerceToBlob(context.entryFields, e.entry),
          result: coerceToBlob(context.resultFields, e.metric),
          connections: appointmentsToInput(e.connections),
          // A named lineup carries its place and its position; an unnamed one is
          // just who played.
          members: places.length
            ? e.members
                .map((participant, index) => ({
                  participant,
                  ordinal: index + 1,
                  slot: places[index],
                }))
                .filter((m) => m.participant)
            : e.lineup.filter(Boolean).map((participant) => ({ participant })),
        };
      });

    // A side that fields a lineup has to field enough of one. The server allows an
    // empty squad — a card filed before anyone is named — but a fixture entered to
    // be scored needs its people, and a scoring UI builds its picker from them. So
    // the form insists on the type's ``composition.min`` up front, rather than
    // letting an unscoreable fixture through to fault the console later.
    const minMembers = composition?.min ?? 0;
    if (!useGrid && minMembers > 0) {
      const short = entries
        // A TBD placeholder fields nobody yet; it is not "short of a line-up".
        .filter((e) => e.participant && e.participant !== TBD)
        .filter((e) => {
          const named = places.length
            ? e.members.filter(Boolean).length
            : e.lineup.filter(Boolean).length;
          return named < minMembers;
        })
        .map((e) => entrantsByCode.get(e.participant)?.name ?? e.participant);
      if (short.length > 0) {
        setErrors(
          short.map(
            (who) => `${who} needs at least ${minMembers} in the line-up before it can be scored`,
          ),
        );
        setBusy(false);
        return;
      }
    }

    try {
      if (editing) {
        // An edit is a partial amend/correction: the entrants (which is how a
        // TBD is resolved), the schedule and the details, recorded server-side
        // on the append-only amendment log.
        const result = await updateFixtureAction(editing.id, {
          name: effectiveName || null,
          competition: filedUnder || null,
          venue: venue || null,
          scheduledStart: start ? new Date(start).toISOString() : null,
          entries: payloadEntries,
          reason: reason.trim() || undefined,
        });
        if (result.ok) navigate(`/fixtures/${editing.id}`);
        else setErrors([result.error, ...result.violations]);
        return;
      }
      const result = await createFixtureAction({
        sportCode: context.sportCode,
        code: effectiveCode,
        fixtureType: type?.code,
        // Which config it was played to. The rules themselves are copied onto the
        // fixture server-side, so editing the config later cannot rewrite this one.
        formatCode: context.config ?? undefined,
        scoringProgram: scoringProgram || undefined,
        scoringUi: scoringUi || undefined,
        name: effectiveName || undefined,
        competition: filedUnder || undefined,
        venue: venue || undefined,
        scheduledStart: start ? new Date(start).toISOString() : undefined,
        status,
        entries: payloadEntries,
        officials: appointmentsToInput(officials),
        format: coerceToBlob(context.formatFields, format),
      });
      if (result.ok) navigate(`/${context.sportCode}/fixtures`);
      else setErrors([result.error, ...result.violations]);
    } finally {
      // Release the button even if the action rejects at the transport layer —
      // otherwise a dropped connection leaves it stuck on "Creating…".
      setBusy(false);
    }
  }

  if (!type) {
    return (
      <div className={styles.empty}>
        <p className={styles.emptyTitle}>This sport has no kind of fixture yet.</p>
        <p className={styles.emptyText}>
          Open the sport in the wizard and answer the last step — who competes, who is fielded and
          who officiates — and this screen builds itself from the answers.
        </p>
      </div>
    );
  }

  const picker = (
    id: string,
    label: string,
    value: string,
    options: FormOption[],
    onChange: (v: string) => void,
    required = false,
  ) => (
    <div className={styles.field} key={id}>
      <label className={styles.label} htmlFor={id}>
        {label}
        {required && <span className={styles.required}> *</span>}
      </label>
      <select
        id={id}
        className={`${styles.control} ${styles.select}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{options.length ? "Choose…" : "Nothing to choose"}</option>
        {options.map((o) => (
          <option key={o.code} value={o.code}>
            {o.name}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <form className={styles.form} onSubmit={submit}>
      {/* The operator's pick, made where the fixture is entered. A sport can
          carry many scoring workflows; which one scores this fixture is a
          decision, so it is asked — defaulted to the program whose scope best
          matches the kind and format chosen, never silently assigned. */}
      {context.scoringPrograms.length > 0 ? (
        <div className={styles.scoredBy}>
          <label htmlFor="scoring-program">Scoring workflow</label>
          <select
            id="scoring-program"
            value={scoringProgram}
            onChange={(e) => setScoringProgram(e.target.value)}
          >
            {context.scoringPrograms.map((option) => (
              <option key={option.id} value={option.id}>
                {/* An unnamed workflow reads as what it is — open it and click
                    its title to give it a real name. */}
                {`${option.name ?? "Unnamed workflow"} v${option.version} — ${option.scope.length > 0 ? option.scope.join(", ") : "whole sport"}`}
                {option.id === context.suggestedScoringProgram ? " (suggested)" : ""}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <p className={styles.scoredBy} data-missing="true">
          No published scoring workflow yet — this fixture cannot be scored until one is.
        </p>
      )}
      {context.scoringUis.length > 0 && (
        <div className={styles.scoredBy}>
          <label htmlFor="scoring-ui">Scoring UI</label>
          <select id="scoring-ui" value={scoringUi} onChange={(e) => setScoringUi(e.target.value)}>
            {context.scoringUis.map((option) => (
              <option key={option.id} value={option.id}>
                {`${option.name} — ${option.appliesTo.length > 0 ? option.appliesTo.join(", ") : "whole sport"}`}
                {option.id === context.suggestedScoringUi ? " (suggested)" : ""}
              </option>
            ))}
          </select>
        </div>
      )}
      {errors && (
        <div className={styles.errorBox} role="alert">
          {errors.map((e) => (
            <p key={e}>{e}</p>
          ))}
        </div>
      )}

      <section className={styles.card}>
        <h2 className={styles.cardTitle}>Where and when</h2>
        <div className={styles.grid}>
          {/* One picker showing every place this sport allows, each by its full
              path. It replaces a dropdown per level, which had to be walked in
              order and could only ever reach one shape of tree. */}
          <div className={`${styles.field} ${styles.wide}`}>
            <label className={styles.label} htmlFor="filed-under">
              Filed under
            </label>
            <select
              id="filed-under"
              className={`${styles.control} ${styles.select}`}
              value={filedUnder}
              onChange={(e) => setFiledUnder(e.target.value)}
            >
              <option value="">
                {context.containers.length ? "Choose…" : "Nowhere to file it yet"}
              </option>
              {context.containers.map((container) => (
                <option key={container.code} value={container.code}>
                  {container.path}
                </option>
              ))}
            </select>
          </div>
          {picker("venue", "Venue", venue, context.venues, setVenue)}
          <div className={styles.field}>
            <label className={styles.label} htmlFor="start">
              Starts
            </label>
            <input
              id="start"
              type="datetime-local"
              className={styles.control}
              value={start}
              onChange={(e) => setStart(e.target.value)}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="status">
              Status
            </label>
            <select
              id="status"
              className={`${styles.control} ${styles.select}`}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUSES.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="fixture-name">
              Name
            </label>
            <input
              id="fixture-name"
              className={styles.control}
              value={name}
              placeholder={suggestedName || "Named from the entrants"}
              onChange={(e) => setName(e.target.value)}
            />
            <p className={styles.hint}>
              {effectiveCode
                ? "Named from the entrants unless you say otherwise."
                : "Filled in once the entrants are chosen."}
            </p>
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="fixture-code">
              Identifier
            </label>
            <input
              id="fixture-code"
              className={styles.control}
              value={effectiveCode}
              placeholder="derived from the name"
              onChange={(e) => setCode(e.target.value)}
              // A fixture's identifier is fixed once it exists — an edit never
              // renames the record it is editing.
              readOnly={Boolean(editing)}
              disabled={Boolean(editing)}
            />
            <p className={styles.hint}>
              {editing ? "Fixed once the fixture exists." : `Unique within ${context.sportCode}.`}
            </p>
          </div>
        </div>
        {editing && (
          <div className={styles.field}>
            <label className={styles.label} htmlFor="fixture-reason">
              Reason for the change
            </label>
            <input
              id="fixture-reason"
              className={styles.control}
              value={reason}
              placeholder="e.g. SF2 decided; corrected scoresheet"
              onChange={(e) => setReason(e.target.value)}
            />
            <p className={styles.hint}>
              Recorded on the amendment history. A live or finished fixture is edited as an
              append-only correction — the record is never rewritten.
            </p>
          </div>
        )}
      </section>

      <section className={styles.card}>
        <div className={styles.cardHead}>
          <h2 className={styles.cardTitle}>
            {isField ? `The field` : `The ${context.entrantLabel.toLowerCase()}s`}
          </h2>
          <span className={styles.cardNote}>
            {isField
              ? "In finishing order — drag is not needed, the order here is the result."
              : alignments.length > 0
                ? alignments.join(" and ")
                : "Two sides"}
          </span>
        </div>

        {useGrid ? (
          <EntryGrid
            entrants={context.entrants}
            fields={context.entryFields}
            entrantLabel={context.entrantLabel}
            rows={gridRows}
            onChange={setGridRows}
            connections={context.connections}
            extraColumns={extraColumns}
            onAddColumn={(role) =>
              setExtraColumns((prev) => ({ ...prev, [role]: (prev[role] ?? 0) + 1 }))
            }
          />
        ) : (
        <div className={styles.entries}>
          {entries.map((entry, i) => (
            <div key={i} className={styles.entry}>
              <div className={styles.entryHead}>
                <span className={styles.entryIndex}>{i + 1}</span>
                <select
                  id={`entrant-${i}`}
                  className={`${styles.control} ${styles.select} ${styles.entrantSelect}`}
                  value={entry.participant}
                  aria-label={`${context.entrantLabel} ${i + 1}`}
                  onChange={(e) =>
                    editEntry(i, {
                      participant: e.target.value,
                      lineup: [],
                      members: places.map(() => ""),
                    })
                  }
                >
                  <option value="">Choose a {context.entrantLabel.toLowerCase()}…</option>
                  {context.entrants.map((o) => (
                    <option key={o.code} value={o.code}>
                      {o.name}
                    </option>
                  ))}
                  {/* A slot whose party is not known yet — "Winner of QF1". */}
                  <option value={TBD}>TBD — to be decided</option>
                </select>
                {entry.participant === TBD && (
                  <input
                    className={`${styles.control} ${styles.entrantSelect}`}
                    value={entry.placeholder}
                    placeholder="e.g. Winner of QF1"
                    aria-label={`TBD label for entry ${i + 1}`}
                    onChange={(e) => editEntry(i, { placeholder: e.target.value })}
                  />
                )}
                {!isField && alignments.length > 0 && (
                  <span className={styles.alignment}>{entry.alignment || alignments[i] || ""}</span>
                )}
                {isField && (
                  <input
                    className={`${styles.control} ${styles.rankInput}`}
                    value={entry.rank}
                    placeholder={`${i + 1}`}
                    aria-label={`Finishing position for entry ${i + 1}`}
                    onChange={(e) => editEntry(i, { rank: e.target.value })}
                  />
                )}
                {entries.length > minEntries && (
                  <button
                    type="button"
                    className={styles.iconButton}
                    aria-label={`Remove entry ${i + 1}`}
                    onClick={() => setEntries(entries.filter((_, j) => j !== i))}
                  >
                    <IconX size={15} />
                  </button>
                )}
              </div>

              {context.connections.length > 0 && (
                <div className={styles.slotRow}>
                  {context.connections.map((slot) => (
                    <Appointments
                      key={slot.role}
                      slot={slot}
                      idPrefix={`conn-${i}`}
                      chosen={entry.connections[slot.role] ?? []}
                      onChange={(next) =>
                        editEntry(i, {
                          connections: { ...entry.connections, [slot.role]: next },
                        })
                      }
                    />
                  ))}
                </div>
              )}

              {context.entryFields.length > 0 && (
                <div className={styles.slotRow}>
                  <FieldSetForm
                    fields={context.entryFields}
                    values={entry.entry}
                    idPrefix={`draw-${i}`}
                    onChange={(key, value) =>
                      editEntry(i, { entry: { ...entry.entry, [key]: value } })
                    }
                  />
                </div>
              )}

              {places.length > 0 && (
                <div className={styles.legs}>
                  {/* Shown before an entrant is chosen: four empty places say
                      "this needs four" far better than a count in a hint. */}
                  <p className={styles.legsHead}>
                    <IconUsers size={14} /> {context.lineupLabel ?? "Lineup"} — in order
                  </p>
                  {places.map((place, leg) => (
                    <div className={styles.leg} key={place}>
                      <span className={styles.legName}>{place}</span>
                      <select
                        className={`${styles.control} ${styles.select}`}
                        value={entry.members[leg] ?? ""}
                        aria-label={`${place} for entry ${i + 1}`}
                        onChange={(e) =>
                          editEntry(i, {
                            members: entry.members.map((m, j) =>
                              j === leg ? e.target.value : m,
                            ),
                          })
                        }
                      >
                        <option value="">Choose…</option>
                        {lineupFor(entry).map((person) => (
                          <option key={person.code} value={person.code}>
                            {person.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              )}

              {places.length === 0 && context.lineupLabel && entry.participant && (
                <details className={styles.lineup}>
                  <summary className={styles.lineupSummary}>
                    <IconUsers size={14} />
                    {context.lineupLabel}s
                    <span className={styles.lineupCount}>{entry.lineup.filter(Boolean).length}</span>
                  </summary>
                  <div className={styles.lineupGrid}>
                    {lineupFor(entry).map((player) => {
                      const on = entry.lineup.includes(player.code);
                      return (
                        <button
                          key={player.code}
                          type="button"
                          className={`${styles.player} ${on ? styles.playerOn : ""}`}
                          onClick={() =>
                            editEntry(i, {
                              lineup: on
                                ? entry.lineup.filter((c) => c !== player.code)
                                : [...entry.lineup, player.code],
                            })
                          }
                        >
                          {player.name}
                        </button>
                      );
                    })}
                    {lineupFor(entry).length === 0 && (
                      <p className={styles.hint}>
                        No {context.lineupLabel.toLowerCase()}s belong to this{" "}
                        {context.entrantLabel.toLowerCase()} yet.
                      </p>
                    )}
                  </div>
                </details>
              )}

              {context.resultFields.length > 0 && (
                <details className={styles.lineup}>
                  <summary className={styles.lineupSummary}>Result and figures</summary>
                  <div className={styles.metricGrid}>
                    <FieldSetForm
                      fields={context.resultFields}
                      values={entry.metric}
                      idPrefix={`metric-${i}`}
                      onChange={(key, value) =>
                        editEntry(i, { metric: { ...entry.metric, [key]: value } })
                      }
                    />
                  </div>
                </details>
              )}
            </div>
          ))}
        </div>
        )}

        {isField && !useGrid && (
          <button
            type="button"
            id="add-entry"
            className={styles.addRow}
            onClick={() => setEntries([...entries, blankEntry("", places.length)])}
          >
            <IconPlus size={15} /> Add a {context.entrantLabel.toLowerCase()}
          </button>
        )}
      </section>

      {context.officials.length > 0 && (
        <section className={styles.card}>
          <h2 className={styles.cardTitle}>Officials</h2>
          <div className={styles.grid}>
            {context.officials.map((slot) => (
              <Appointments
                key={slot.role}
                slot={slot}
                idPrefix="official"
                chosen={officials[slot.role] ?? []}
                onChange={(next) => setOfficials({ ...officials, [slot.role]: next })}
              />
            ))}
          </div>
        </section>
      )}

      {context.formatFields.length > 0 && (
        <section className={styles.card}>
          <h2 className={styles.cardTitle}>How it is played</h2>
          <div className={styles.grid}>
            <FieldSetForm
              fields={context.formatFields}
              values={format}
              idPrefix="format"
              onChange={(key, value) => setFormat({ ...format, [key]: value })}
            />
          </div>
        </section>
      )}

      <div className={styles.actions}>
        <button
          id="create-fixture"
          className={styles.submit}
          type="submit"
          disabled={busy || !effectiveCode}
        >
          {editing
            ? busy
              ? "Saving…"
              : "Save changes"
            : busy
            ? "Creating…"
            : "Create fixture"}
        </button>
      </div>
    </form>
  );
}
